import React, { useRef, useState, useCallback } from "react"
import Webcam from "react-webcam"

/**
 * VideoRecordForm – allows client to record a video via webcam and uploads it to S3
 * Props mirror VideoUploadForm so VideoUploadPage can swap components without changes.
 */
const VideoRecordForm = ({ uploadHash, onUploadStart, onUploadProgress, onUploadSuccess, onUploadError }) => {
  const webcamRef = useRef(null)
  const [recording, setRecording] = useState(false)
  const [recorder, setRecorder] = useState(null)
  const [recordedBlobUrl, setRecordedBlobUrl] = useState(null)
  const [uploadingState, setUploadingState] = useState(false)
  const [countdown, setCountdown] = useState(0) // 0 = no countdown, 3,2,1 = countdown numbers

  // Use ref to store chunks to avoid closure issues
  const chunksRef = useRef([])

  // Start the countdown before recording
  const startRecording = useCallback(() => {
    if (recording || countdown > 0) return

    // Clear previous chunks and recorded video
    chunksRef.current = []
    setRecordedBlobUrl(null)

    // Start countdown
    setCountdown(3)

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval)
          // Start actual recording after countdown
          setTimeout(() => beginActualRecording(), 100)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [recording, countdown])

  // Actually start recording (called after countdown)
  const beginActualRecording = useCallback(() => {
    const videoEl = webcamRef.current?.video || webcamRef.current?.videoEl || null
    const stream = videoEl?.srcObject || webcamRef.current?.stream

    if (!stream) {
      alert("Unable to access camera stream")
      return
    }

    // Pick a MIME type supported by the current browser
    let mimeType = ""
    const preferredTypes = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4" // Safari
    ]
    for (const type of preferredTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        mimeType = type
        break
      }
    }

    const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)

    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data)
      }
    }

    mediaRecorder.onstop = () => {
      const chunks = chunksRef.current
      if (chunks.length === 0) {
        alert("No video data was recorded. Please try again.")
        return
      }
      const blobType = chunks[0].type || mimeType || "video/webm"
      const blob = new Blob(chunks, { type: blobType })
      const url = URL.createObjectURL(blob)
      setRecordedBlobUrl(url)
      // Clear chunks after successful creation
      chunksRef.current = []
    }

    mediaRecorder.start()
    setRecorder(mediaRecorder)
    setRecording(true)
  }, [])

  const stopRecording = useCallback(() => {
    if (recorder && recording) {
      recorder.stop()
      setRecording(false)
    }
    setCountdown(0) // Clear countdown state if any
  }, [recorder, recording])

  // helper to upload blob file via existing presigned flow
  const uploadRecording = async () => {
    if (!recordedBlobUrl || uploadingState) return
    try {
      setUploadingState(true)
      onUploadStart && onUploadStart()

      // Fetch blob from url
      const res = await fetch(recordedBlobUrl)
      const blob = await res.blob()
      const filename = `recorded_${Date.now()}.webm`

      // Get presigned URL
      const presignedResponse = await fetch(`/video-review/upload/${uploadHash}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename,
          content_type: blob.type,
          file_size: blob.size
        })
      })
      const presignedData = await presignedResponse.json()
      if (!presignedData.success) throw new Error(presignedData.error || "Failed to get upload URL")

      // Upload to S3 using XMLHttpRequest to track progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.upload.addEventListener("progress", (evt) => {
          if (evt.lengthComputable) {
            const pct = Math.round((evt.loaded / evt.total) * 90)
            onUploadProgress && onUploadProgress(pct)
          }
        })
        xhr.addEventListener("load", () => {
          if (xhr.status === 200 || xhr.status === 204) resolve()
          else reject(new Error("Upload failed"))
        })
        xhr.addEventListener("error", () => reject(new Error("Upload failed")))
        xhr.open("PUT", presignedData.data.upload_url)
        xhr.setRequestHeader("Content-Type", blob.type)
        xhr.send(blob)
      })

      // Complete upload
      onUploadProgress && onUploadProgress(95)
      const completeResp = await fetch(`/video-review/upload/${uploadHash}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          video_key: presignedData.data.video_key,
          filename,
          content_type: blob.type,
          file_size: blob.size
        })
      })
      const completeData = await completeResp.json()
      if (!completeData.success) throw new Error(completeData.error || "Failed to complete upload")

      onUploadProgress && onUploadProgress(100)
      onUploadSuccess && onUploadSuccess()
    } catch (err) {
      console.error("Recording upload error", err) // eslint-disable-line no-console
      onUploadError && onUploadError(err.message)
    } finally {
      setUploadingState(false)
    }
  }

  const resetRecording = useCallback(() => {
    if (recordedBlobUrl) {
      URL.revokeObjectURL(recordedBlobUrl)
    }
    setRecordedBlobUrl(null)
    setCountdown(0) // Reset countdown state
    chunksRef.current = []
  }, [recordedBlobUrl])

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Camera Preview - Only show when recording or no recorded video */}
      {(!recordedBlobUrl || recording) && (
        <div className="h-full w-full">
          <Webcam
            audio={true}
            audioConstraints={{ echoCancellation: false, autoGainControl: true }}
            ref={webcamRef}
            mirrored={false}
            videoConstraints={{ facingMode: "user" }}
            className="h-full w-full object-cover"
          />
        </div>
      )}

      {/* Playback after recording - Full screen */}
      {!recording && recordedBlobUrl && (
        <div className="absolute inset-0 h-full w-full">
          <video className="h-full w-full object-cover" src={recordedBlobUrl} controls />
        </div>
      )}

      {/* Countdown Overlay */}
      {countdown > 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-center">
            <div className="animate-pulse text-8xl font-bold text-white">{countdown}</div>
            <div className="mt-4 text-xl text-white">Get ready to record...</div>
          </div>
        </div>
      )}

      {/* Action Bar - Overlaid on video */}
      <div className="absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center justify-center space-x-4">
        {/* Idle state */}
        {!recording && !recordedBlobUrl && countdown === 0 && (
          <button
            onClick={startRecording}
            className="flex items-center justify-center gap-2 rounded-md bg-red-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:bg-red-500">
            <i className="icon-film top-0 text-white-important-before" />
            Record
          </button>
        )}

        {/* Countdown state */}
        {countdown > 0 && (
          <button
            disabled
            className="flex cursor-not-allowed items-center justify-center gap-2 rounded-md bg-red-600/50 px-8 py-4 text-lg font-semibold text-white shadow-lg">
            <i className="icon-film top-0 text-white-important-before" />
            Starting...
          </button>
        )}

        {/* Recording state */}
        {recording && (
          <button
            onClick={stopRecording}
            className="flex items-center justify-center gap-2 rounded-md bg-white/20 px-8 py-4 text-lg font-semibold text-white shadow-lg backdrop-blur hover:bg-white/30">
            <span className="block h-4 w-4 rounded-sm bg-red-600" />
            Stop
          </button>
        )}

        {/* Recorded state */}
        {!recording && recordedBlobUrl && (
          <>
            <button
              onClick={resetRecording}
              className="flex items-center justify-center gap-2 rounded-md bg-white/20 px-6 py-4 text-white shadow-lg backdrop-blur hover:bg-white/30">
              <i className="icon-recurring top-0 text-white-important-before" />
              Redo
            </button>
            <button
              onClick={uploadRecording}
              disabled={uploadingState}
              className="flex items-center justify-center gap-2 rounded-md bg-teal px-6 py-4 font-semibold text-white shadow-lg hover:bg-teal-dark disabled:opacity-50">
              <i className="icon-send top-0 text-white-important-before" />
              {uploadingState ? "Uploading" : "Submit"}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default VideoRecordForm
